// contexts/LanguageContext.js
// ─────────────────────────────────────────────────────────────────────────────
// Bilingual (EN / ZH) language system for gtamelb.
//
// - Reads an initial language from localStorage → browser language → 'en'.
// - Exposes { lang, setLang, t } via the useLanguage() hook.
// - t('some.key', { name: 'Adam' }) returns the translated string with {var}
//   interpolation, falling back to English, then to the raw key.
// - When a user is logged in, the chosen language is persisted to their Firestore
//   profile (users/{uid}.preferredLanguage) and re-adopted on next login.
//
// IMPORTANT: <LanguageProvider> must be rendered INSIDE <AuthProvider> because it
// calls useAuth(). See App.js wiring notes.
// ─────────────────────────────────────────────────────────────────────────────
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import { translations } from '../i18n/translations';

const LS_KEY = 'gtamelb_lang';
export const SUPPORTED_LANGS = ['en', 'zh'];
export const LANG_LABELS = { en: 'English', zh: '中文' };

function detectInitialLang() {
  try {
    const saved = localStorage.getItem(LS_KEY);
    if (saved && SUPPORTED_LANGS.includes(saved)) return saved;
  } catch { /* private mode */ }
  try {
    const nav = (navigator.language || navigator.userLanguage || 'en').toLowerCase();
    if (nav.startsWith('zh')) return 'zh';
  } catch { /* SSR */ }
  return 'en';
}

const LanguageContext = createContext({
  lang: 'en',
  setLang: () => {},
  t: (k) => k,
  supported: SUPPORTED_LANGS,
});

export function LanguageProvider({ children }) {
  // useAuth() may be undefined if (by mistake) this is mounted outside AuthProvider.
  const auth = useAuth() || {};
  const currentUser = auth.currentUser || null;

  const [lang, setLangState] = useState(detectInitialLang);
  // Tracks whether the user explicitly picked a language this session. If so, we
  // don't let a stale Firestore value override their fresh choice.
  const userTouched = useRef(false);

  const applyLang = useCallback(
    (next, { persistRemote = true } = {}) => {
      if (!SUPPORTED_LANGS.includes(next)) return;
      setLangState(next);
      try { localStorage.setItem(LS_KEY, next); } catch { /* ignore */ }
      try { document.documentElement.lang = next === 'zh' ? 'zh-CN' : 'en'; } catch { /* SSR */ }

      if (persistRemote && currentUser?.uid) {
        const ref = doc(db, 'users', currentUser.uid);
        updateDoc(ref, { preferredLanguage: next }).catch(async () => {
          // Doc may not exist yet — fall back to a merge set.
          try { await setDoc(ref, { preferredLanguage: next }, { merge: true }); } catch { /* best-effort */ }
        });
      }
    },
    [currentUser]
  );

  // Public setter — flags that the choice was user-initiated.
  const setLang = useCallback(
    (next) => {
      userTouched.current = true;
      applyLang(next);
    },
    [applyLang]
  );

  // On login (or user change) adopt the saved preference, unless the user has
  // already made a choice this session.
  useEffect(() => {
    if (!currentUser?.uid) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', currentUser.uid));
        if (cancelled) return;
        const pref = snap.exists() ? snap.data().preferredLanguage : null;
        if (pref && SUPPORTED_LANGS.includes(pref) && !userTouched.current) {
          applyLang(pref, { persistRemote: false });
        } else if (!pref && userTouched.current) {
          // User chose a language before their profile had the field — sync it up.
          applyLang(lang);
        }
      } catch { /* offline / permissions — keep local choice */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, applyLang]);

  const t = useCallback(
    (key, vars) => {
      const table = translations[lang] || translations.en;
      let str;
      if (table && Object.prototype.hasOwnProperty.call(table, key)) str = table[key];
      else if (Object.prototype.hasOwnProperty.call(translations.en, key)) str = translations.en[key];
      else str = key; // last-resort fallback shows the key so missing strings are obvious
      if (vars && typeof str === 'string') {
        str = str.replace(/\{(\w+)\}/g, (_, k) => (vars[k] != null ? String(vars[k]) : `{${k}}`));
      }
      return str;
    },
    [lang]
  );

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, supported: SUPPORTED_LANGS }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}

export default LanguageContext;
